import { Table, Tag } from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { DayDetailDrawer } from "@/modules/attendance/components/DayDetailDrawer";
import { ATTENDANCE_STATUS_LABELS } from "@/modules/attendance/types";
import type { EmployeeAttendanceDay } from "../hooks/useEmployees";
import { recentRows } from "../utils/recentRows";

/** Kanvasdagi jadval beshta qatorni ko'rsatadi. */
const ROWS = 5;

/** ISO timestamptz → mahalliy `HH:mm`. Bo'sh qiymat uchun «—». */
function soat(value: string | null): string {
	return value ? dayjs(value).format("HH:mm") : "—";
}

/**
 * Nol daqiqa — «—», `0` EMAS.
 *
 * ⚠️ `0` «kechikish o'lchandi va u nolga teng» degan ma'noni berardi;
 * kechikish YO'Q holati esa boshqa gap (`avgLateMin` ning `null`
 * konvensiyasi bilan bir xil mantiq).
 */
function daqiqa(value: number): string {
	return value > 0 ? String(value) : "—";
}

/**
 * «Oxirgi davomat kunlari» — kanvasdagi jadval.
 *
 * ⚠️⚠️ **Yangi so'rov YUBORMAYDI** — `days` allaqachon «Umumiy» yorlig'i
 * uchun olingan oynadan keladi. Alohida so'rov AYNI ma'lumotni ikkinchi
 * marta tortardi.
 *
 * ⚠️⚠️ **Tip `EmployeeAttendanceDay`, `AttendanceDayLike` EMAS.** Qator
 * bosilganda `DayDetailDrawer` ochiladi va u `attendance_days.id` ni
 * kutadi; `AttendanceDayLike` da `id` yo'q. `GET /attendance/days`
 * javobida u ALLAQACHON bor — faqat lokal tip uni sanamasdi.
 *
 * ⚠️ **Kanvasdagi «Hudud» ustuni va «Ta'til arizasi #4182» katagi
 * CHIZILMAYDI**: `AttendanceDaySchema` da na `zoneId`, na hudud nomi, na
 * arizaga havola bor. Bo'sh ustunni «keyin to'ldiramiz» deb qoldirish
 * `EmployeeProfileHeader` da yozilgan qoidani buzardi va foydalanuvchiga
 * doim bo'sh ustun ko'rsatardi.
 *
 * ⚠️ **Sarlavhadagi havola RAQAMSIZ** (kanvasda «Barchasi (128) →»):
 * sanoq endpointi yo'q va uni olish uchun qo'shimcha so'rov
 * `destroyOnHidden` qarorining butun asosini bekor qilardi.
 */
export function RecentDaysTable({ days }: { days: EmployeeAttendanceDay[] }) {
	const { t } = useTranslation("employees");
	const [ochiqKun, setOchiqKun] = useState<string | null>(null);

	const columns: ColumnsType<EmployeeAttendanceDay> = [
		{ title: t("recentDaysTable.date"), dataIndex: "workDate", key: "workDate" },
		{
			title: t("recentDaysTable.status"),
			key: "status",
			render: (_, row) => (
				<Tag color={ATTENDANCE_STATUS_LABELS[row.status].color}>
					{ATTENDANCE_STATUS_LABELS[row.status].label}
				</Tag>
			),
		},
		{
			title: t("recentDaysTable.checkIn"),
			key: "in",
			render: (_, row) => soat(row.firstCheckInAt),
		},
		{
			title: t("recentDaysTable.checkOut"),
			key: "out",
			render: (_, row) => soat(row.lastCheckOutAt),
		},
		{
			title: t("recentDaysTable.late"),
			key: "lateMinutes",
			align: "right",
			render: (_, row) => daqiqa(row.lateMinutes),
		},
		{
			title: t("recentDaysTable.inZone"),
			key: "timeInZoneMin",
			align: "right",
			render: (_, row) => daqiqa(row.timeInZoneMin),
		},
		{
			title: t("recentDaysTable.departures"),
			key: "departuresCount",
			align: "right",
			render: (_, row) => row.departuresCount,
		},
	];

	return (
		<>
			<Table<EmployeeAttendanceDay>
				rowKey="id"
				size="small"
				pagination={false}
				dataSource={recentRows(days, ROWS)}
				columns={columns}
				onRow={(row) => ({ onClick: () => setOchiqKun(row.id) })}
			/>

			<DayDetailDrawer dayId={ochiqKun} onClose={() => setOchiqKun(null)} />
		</>
	);
}
